import type { Matrix } from '@/models/Matrix';
import { type ChangeSet, type ChangeSetNonOptional, CREATE, DELETE, UPDATE, type UpdateStep } from './WebSocketMessage';
import type { Box } from './types';
import { Point } from '@/models/Point';

export function htmlId(id: string) {
  return '_' + id;
}

export function getCoordsFromEvent(ev: any) {
  if (ev.changedTouches) {
    ev = ev.changedTouches[0];
  }
  return new Point({ x: ev.clientX, y: ev.clientY });
}

export function getPointFromEvent(
  ev: MouseEvent | TouchEvent,
  matrix?: Matrix | undefined,
  inverse = false,
  { x = 0, y = 0 } = {},
) {
  const p = getCoordsFromEvent(ev);
  p.x += x;
  p.y += y;

  if (!matrix) return p;

  matrix = inverse && matrix ? matrix.inverse() : matrix;
  return p.transformO(matrix as Matrix);
}

export function minMaxPoints(points: Point[], cb: (xMin: number, xMax: number, yMin: number, yMax: number) => any) {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  points.forEach((p) => {
    xMin = Math.min(xMin, p.x);
    xMax = Math.max(xMax, p.x);
    yMin = Math.min(yMin, p.y);
    yMax = Math.max(yMax, p.y);
  });

  return cb(xMin, xMax, yMin, yMax);
}

export function minMaxArray(points: Point[]): number[] {
  return minMaxPoints(points, (...args) => args);
}

export function bboxPoints(points: Point[]): [Point, Point, Point, Point] {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  points.forEach((p) => {
    xMin = Math.min(xMin, p.x);
    xMax = Math.max(xMax, p.x);
    yMin = Math.min(yMin, p.y);
    yMax = Math.max(yMax, p.y);
  });

  return minMaxPoints(points, (xMin, xMax, yMin, yMax) => {
    return [
      new Point({ x: xMin, y: yMin }),
      new Point({ x: xMax, y: yMin }),
      new Point({ x: xMax, y: yMax }),
      new Point({ x: xMin, y: yMax }),
    ];
  });
}

const box = (xMin: number, xMax: number, yMin: number, yMax: number): [number, number, number, number] => [
  xMin,
  yMin,
  xMax - xMin,
  yMax - yMin,
];

export function bbox(points: Point[]): number[] {
  return minMaxPoints(points, box);
}

export function pureBBox(points: [Point, Point, Point, Point]) {
  const { x: xMin, y: yMin } = points[0];
  const { x: xMax, y: yMax } = points[2];

  return box(xMin, xMax, yMin, yMax);
}

export function boxToPoints(box: { x: number; y: number; width: number; height: number }) {
  const { x, y, width, height } = box;

  return [
    new Point({ x, y }),
    new Point({ x: x + width, y }),
    new Point({ x: x + width, y: y + height }),
    new Point({ x: x, y: y + height }),
  ];
}

export function radians(d: number) {
  return ((d % 360) * Math.PI) / 180;
}

export function aspectRatioPoints(points: Point[], isLine = false, ignoreFix = false) {
  const p1 = points[points.length - 1];
  const p2 = points[points.length - 2];
  if (!p1 || !p2 || (p1.fixed && !ignoreFix)) return;

  const vec = p1.sub(p2);
  const len = Math.max(Math.abs(vec.x), Math.abs(vec.y));

  const candidates = [p2.add({ x: len * Math.sign(vec.x), y: len * Math.sign(vec.y) } as Point)];

  if (isLine) {
    candidates.push(new Point({ x: p2.x, y: p1.y }), new Point({ x: p1.x, y: p2.y }));
  }

  const mins = candidates.map((p) => p.sub(p1).length());

  const min = Math.min(...mins);
  const index = mins.indexOf(min);

  const { x, y } = candidates[index];
  Object.assign(p1, { x, y });
}

export const intersects = (a: Box, b: Box) => {
  return (
    Math.abs(a.x + a.width / 2 - (b.x + b.width / 2)) * 2 < a.width + b.width &&
    Math.abs(a.y + a.height / 2 - (b.y + b.height / 2)) * 2 < a.height + b.height
  );
};

const clone = (obj: any) => {
  return JSON.parse(JSON.stringify(obj));
};

export const mergeChangeSet = (newSet: ChangeSetNonOptional, oldSet: ChangeSetNonOptional, backwards = false) => {
  newSet = clone(newSet);
  oldSet = clone(oldSet);

  for (const [modelName, changes] of Object.entries(newSet)) {
    if (!oldSet[modelName]) oldSet[modelName] = {} as ChangeSetNonOptional[string];

    if (!oldSet[modelName][CREATE]) {
      if (changes[backwards ? DELETE : CREATE]) {
        // Creation doesnt exist in oldSet but newSet, so we just copy it
        oldSet[modelName][CREATE] = changes[backwards ? DELETE : CREATE];
      }
    } else {
      // Creation exists in oldSet, so we overwrite every model
      for (const [_id, record] of Object.entries(changes[backwards ? DELETE : CREATE] || {})) {
        oldSet[modelName][CREATE][_id] = record;
        // In case the old set has a deletion for this object we need to remove it
        // because its invalidated by the new creation
        if (oldSet[modelName][DELETE]?.[_id]) {
          delete oldSet[modelName][DELETE][_id];
        }
      }
    }

    if (!oldSet[modelName][DELETE]) {
      if (changes[backwards ? CREATE : DELETE]) {
        // Deletion doesnt exist in oldSet but newSet, so we just copy it
        oldSet[modelName][DELETE] = changes[backwards ? CREATE : DELETE];
      }
    } else {
      // Deletion exists in oldSet, so we overwrite every model
      for (const [_id, record] of Object.entries(changes[backwards ? CREATE : DELETE] || {})) {
        oldSet[modelName][DELETE][_id] = record;
        // In case the old set has a creation for this object we need to remove it
        // because its invalidated by the new deletion
        if (oldSet[modelName][CREATE]?.[_id]) {
          delete oldSet[modelName][CREATE][_id];
        }
      }
    }

    if (!oldSet[modelName][UPDATE]) {
      if (changes[UPDATE]) {
        // Update doesnt exist in oldSet but newSet, so we just copy it
        // For backwards we have to reverse the update steps and swap old and new value

        oldSet[modelName][UPDATE] = {};

        if (backwards) {
          for (const [_id, updates] of Object.entries(changes[UPDATE])) {
            oldSet[modelName][UPDATE][_id] = updates
              .reverse()
              .map(([cmd, key, value, oldValue]) => [cmd, key, oldValue, value]);
          }
        } else {
          oldSet[modelName][UPDATE] = changes[UPDATE];
        }
      }
    } else {
      // Update exists in oldSet, so we overwrite every model
      for (let [_id, record] of Object.entries(changes[UPDATE] || {})) {
        oldSet[modelName][UPDATE][_id] = oldSet[modelName][UPDATE][_id] || [];

        if (backwards) {
          record = record.reverse().map(([cmd, key, value, oldValue]) => [cmd, key, oldValue, value]);
        }

        //oldSet[modelName][UPDATE][_id].push(...record);  (This line, which was replaced below, had the possibility of leading to a "Maximum call stack size exceeded" error.)
        oldSet[modelName][UPDATE][_id] = oldSet[modelName][UPDATE][_id].concat(record);
      }
    }
  }

  cleanUpChangeSet(oldSet);

  return oldSet;
};

// This function removes state that counteracts itself
// e.g. if an object is created AND deleted, we remove both changes
// if there isnt any change in update, create or delete, we remove the whole section
export const cleanUpStateChangeSet = (changeSet: ChangeSetNonOptional) => {
  for (const changes of Object.values(changeSet)) {
    if (changes[DELETE]) {
      for (const _id of Object.keys(changes[DELETE])) {
        if (changes[CREATE]?.[_id]) {
          delete changes[CREATE][_id];
          delete changes[DELETE][_id];
        }

        if (changes[UPDATE]?.[_id]) {
          delete changes[UPDATE][_id];
        }
      }
      if (!Object.keys(changes[CREATE] || {}).length) {
        delete (changes as ChangeSet)[CREATE];
      }

      if (!Object.keys(changes[DELETE] || {}).length) {
        delete (changes as ChangeSet)[DELETE];
      }
    }
  }
  return changeSet;
};

// This function merges together multiple steps that could be one step.
// e.g. if something is dragged, the transform matrix changes for every mousemove
// and evertime only e and f are changed. What matters however, is the last value of e and f
export function mergeUpdateSteps(steps: UpdateStep[]) {
  return steps;
  // for (let i = steps.length; i--;) {
  //   const step = steps[i]
  //   const prevStep = steps[i - 1]

  //   if (!prevStep) continue

  //   const cmd = step[0]
  //   const prevCmd = prevStep[0]

  //   if (cmd === '$set' && prevCmd === '$set') {
  //     const [ , key, newValue ] = step
  //     prevStep[2] = Object.assign({}, prevStep[2], { [key]: newValue })
  //     steps.pop()
  //   }
  // }
}

export function cleanUpChangeSet(changeSet: ChangeSet) {
  for (const modelName in changeSet) {
    const changes = changeSet[modelName] as ChangeSetNonOptional[string];
    if (!(UPDATE in changes)) {
      continue;
    }

    // if we find updates, merge all steps as much as possible
    // if there are non, delte the whole update
    for (const id in changes[UPDATE]) {
      const steps = changes[UPDATE][id];
      if (steps.length === 0) {
        delete changes[UPDATE][id];
      }

      mergeUpdateSteps(steps);
    }

    // if something is created and updated right afterwards,
    // update the created object to its new values and delete change-steps
    if (changes[CREATE]) {
      for (const id in changes[CREATE]) {
        if (!changes[UPDATE][id]) continue;

        const steps = changes[UPDATE][id];

        for (let i = 0; i < steps.length; ++i) {
          const [cmd, key, value] = steps[i];
          if (cmd !== '$set') break;

          const path = key.split('.');
          const prop = path.pop() as string;

          const m = path.reduce(
            (obj, prop) => {
              if (obj[prop] === undefined) {
                obj[prop] = {};
              }

              return obj[prop] as unknown as Record<string, unknown>;
            },
            changes[CREATE][id] as unknown as Record<string, unknown>,
          );

          m[prop] = value;

          steps.shift();
          --i;
        }

        if (steps.length === 0) {
          delete changes[UPDATE][id];
        }
      }
    }

    // if nothing is left in update, get rid of it
    if (Object.keys(changes[UPDATE]).length === 0) {
      delete (changes as any)[UPDATE];
    }

    // if nothing is left to do for this model after cleanup, get rid of it
    if (Object.keys(changes).length === 0) {
      delete changeSet[modelName];
    }
  }
}

export const getObjByPath = (obj: any, parts: string[]) => {
  let current = obj;
  for (const part of parts) {
    current = current[part];
  }
  return current;
};

export function assert(condition: boolean | object | string | undefined | null, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message ?? 'Assertion failed');
  }
}

export const throttle = (func: (...args: any[]) => any, wait: number) => {
  let timeout: number | null = null;
  return (...args: any[]) => {
    if (timeout) {
      return;
    }
    timeout = window.setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
};

/**
 * This is Object.keys but typed so that it will return a union of all possible keys
 * @param obj The object whose keys are to be returned
 * @returns keys of the object
 */
export const objectKeys = <T extends object, K extends keyof T = keyof T>(obj: T | undefined): K[] =>
  Object.keys(obj ?? {}) as K[];

export const objectValues = <T extends object, K extends keyof T = keyof T>(obj: T | undefined): T[K][] =>
  Object.values(obj ?? {}) as T[K][];
